import { Button, Group, Popover, Stack, Text, Title, toast } from '@common/ui'
import { ReactNode, useState } from 'react'
import { com } from '@eidu/web'
import DraftFieldValue, { draftFieldValueToFieldValue } from '../../domain/entity/DraftFieldValue'
import useReferenceFieldError from '../../domain/entity/useReferenceFieldError'
import { useModifyEntities } from '../../io/useEntityMutations'
import EntityFormInput from '../input/EntityFormInput'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import Field = com.eidu.entity.field.Field
import entityIdFromString = com.eidu.entity.entityIdFromString

export type BulkEditMenuProps = {
  field: Field
  opened: boolean
  onClose: () => void
  target: ReactNode
  selectedEntityIds: string[]
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  onEditComplete: () => void
}

type BulkEditFormProps = Omit<BulkEditMenuProps, 'opened' | 'target'>

const BulkEditForm = ({ field, onClose, selectedEntityIds, entityTypes, onEditComplete }: BulkEditFormProps) => {
  const modifyEntitiesMutation = useModifyEntities()

  const defaultFieldValue = { fieldId: field.id, type: field.type, value: '' }
  const [fieldValue, setFieldValue] = useState<DraftFieldValue>(defaultFieldValue)

  const [isEditing, setIsEditing] = useState(false)

  const { hasError, loading: loadingSelectedEntity } = useReferenceFieldError(field, fieldValue)

  return (
    <Stack gap="md" miw={300}>
      <Title order={4}>Field ID</Title>
      <Text c="dimmed">{field.id.toString()}</Text>
      <Title order={4}>Edit selected items</Title>
      <EntityFormInput
        key={`column-form-field-${field.id.toString()}`}
        field={field}
        fieldValue={fieldValue}
        entityTypes={entityTypes}
        updateField={setFieldValue}
        dropdownWithinPortal={false}
      />
      {selectedEntityIds.length === 0 && (
        <Text size="sm" fs="italic" c="dimmed">
          Please select one or more rows for batch editing
        </Text>
      )}
      <Group justify="flex-end">
        <Button
          disabled={!selectedEntityIds.length || hasError || loadingSelectedEntity || isEditing}
          loading={isEditing}
          onClick={async () => {
            setIsEditing(true)
            try {
              const fieldValuesById = new Map([[field.id, draftFieldValueToFieldValue(fieldValue)]])
              await modifyEntitiesMutation.mutateAsync(
                new Map(
                  selectedEntityIds.map((id) => [
                    entityIdFromString(id),
                    {
                      valuesToUpdateByFieldId: fieldValuesById,
                      linksToUpdate: new Map(),
                      externalValuesToUpdate: new Map(),
                    },
                  ])
                )
              )
              toast.positive(`Updated ${selectedEntityIds.length} items`)
              onEditComplete()
              onClose()
            } catch (e) {
              console.error(e)
              toast.negative(`Failed to update ${selectedEntityIds.length} items`)
            }
            setIsEditing(false)
          }}
        >
          {`Apply (${selectedEntityIds.length})`}
        </Button>
      </Group>
    </Stack>
  )
}

const BulkEditMenu = ({ opened, target, ...formProps }: BulkEditMenuProps) => (
  <Popover
    opened={opened}
    onChange={(isOpened) => {
      if (!isOpened) formProps.onClose()
    }}
    position="bottom-end"
    shadow="md"
    trapFocus
  >
    <Popover.Target>{target}</Popover.Target>
    <Popover.Dropdown p="md">
      <BulkEditForm {...formProps} />
    </Popover.Dropdown>
  </Popover>
)

export default BulkEditMenu
